/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IProductSpecificationViewProps } from '@msdyn365-commerce-modules/product-specification';
import { WS_DELIVERY_TIME } from '../../../configuration/attributes';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ISpecificationCellProps {
    productName?: string;
    cellData?: React.ReactNode;
}

interface ICellContentProps {
    children?: unknown;
    dangerouslySetInnerHTML?: { __html: string };
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Cell values that represent "no data" and should cause a row to be hidden. */
const EMPTY_CELL_VALUES = new Set(['-', '0']);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Extracts the visible text from a specification cell's React element. */
const extractCellText = (cellData: React.ReactNode): string => {
    if (!React.isValidElement(cellData)) {
        return '';
    }
    const { dangerouslySetInnerHTML, children } = cellData.props as ICellContentProps;
    return dangerouslySetInnerHTML ? dangerouslySetInnerHTML.__html.trim() : String(children ?? '');
};

/**
 * Returns `true` when a specification row should be excluded from the table.
 *
 * A row is hidden when any of its cell children either:
 * - matches the delivery-time attribute name, or
 * - contains a placeholder empty value (`-` or `0`).
 */
const shouldHideRow = (row: React.ReactNode): boolean => {
    if (!React.isValidElement(row)) {
        return false;
    }

    return React.Children.toArray((row as React.ReactElement).props.children).some(child => {
        if (!React.isValidElement(child)) {
            return false;
        }

        const { productName, cellData } = child.props as ISpecificationCellProps;

        return productName === WS_DELIVERY_TIME || EMPTY_CELL_VALUES.has(extractCellText(cellData));
    });
};

// ─── Component ────────────────────────────────────────────────────────────────

const ProductSpecificationView: React.FC<IProductSpecificationViewProps> = props => {
    const { ProductSpecification, ProductSpecificationTableProps, ProductSpecificationTableBodyProps, productSpecificationResult } = props;

    const filteredRowData = ArrayExtensions.hasElements(productSpecificationResult)
        ? productSpecificationResult.filter(row => !ObjectExtensions.isNullOrUndefined(row) && !shouldHideRow(row))
        : undefined;

    if (!filteredRowData || !ArrayExtensions.hasElements(filteredRowData)) {
        return null;
    }

    const tableBodyProps = { ...ProductSpecificationTableBodyProps, children: filteredRowData };

    return (
        <Module {...ProductSpecification}>
            {props.title}
            <Node {...ProductSpecificationTableProps}>
                <Node {...tableBodyProps} />
            </Node>
        </Module>
    );
};

export default ProductSpecificationView;
